import React, { useCallback } from 'react'
import { Theme, variantProvider } from '@/app'
import { contributeStore } from '@/stores'
import { FileInput, useFileInput } from '@codeleap/web'
import { Button, View, Player, Icon, CropPickerInput, useCropPickerInputRef } from '@/components'
import { AnyFunction, WebInputFile } from '@codeleap/types'
import { ContributeSizeAlert } from './SizeAlert'
import { verifyFileSize } from '@/utils'

type ContributeFilePreviewProps = {
  type: 'photo' | 'video' | 'text'
  renderContent?: ({ onChange }: { onChange: AnyFunction }) => JSX.Element
}

export const ContributeFilePreview = (props: ContributeFilePreviewProps) => {
  const {
    type,
    renderContent: Content = ({ onChange }) => <ContributeFilePreview.ChangeButton onChange={onChange} style={styles.changeButton} />
  } = props

  const cropPickerInput = useCropPickerInputRef()
  const videoInput = useFileInput()

  const file = contributeStore.state.use(s => s.file)

  const handleChangeFile = useCallback(async () => {
    let files: WebInputFile[] = null

    if (type === 'photo') {
      files = [await cropPickerInput.pickAndCropImage()]
    } else {
      files = await videoInput.openFilePicker()
    }

    const file = files?.length >= 1 ? files[0] : null

    if (file == null) return

    if (type === 'video') {
      const validSize = verifyFileSize(file, 500)

      if (!validSize) {
        return ContributeSizeAlert.open()
      }
    }

    contributeStore.state.set({ file })
  }, [])

  const isPhoto = type === 'photo'

  const placeholder = (
    <View variants={['bg:neutral4', 'center', 'fullWidth']}>
      <Icon
        variants={['iconSize:3']}
        color={Theme.colors.light.neutral5}
        debugName=''
        name={isPhoto ? 'image' : 'video'}
      />
    </View>
  )

  return <>
    <View style={styles.wrapper}>
      {!file?.preview ? placeholder : isPhoto ? <img src={file?.preview} style={styles.preview} /> : (
        <Player
          url={file?.preview}
          styles={{ wrapper: styles.preview }}
          hideControls={false}
        />
      )}

      <Content onChange={handleChangeFile} />
    </View>

    <FileInput multiple={false} ref={videoInput.ref} accept='video/*' />
    <CropPickerInput ref={cropPickerInput.ref} />
  </>
}

ContributeFilePreview.ChangeButton = ({ onChange, style = {} }) => (
  <Button
    debugName=''
    onPress={onChange}
    text='Change'
    style={style}
    variants={['outline', 'large']}
    icon='redo'
  />
)

const width = 340
const aspectRatio = 9 / 16

const styles = variantProvider.createComponentStyle(theme => ({
  wrapper: {
    width,
    aspectRatio,
    borderRadius: 24,
    overflow: 'hidden',
    background: theme.colors.neutral3,
    ...theme.presets.relative,

    [theme.media.down('small')]: {
      borderRadius: 0,
      ...theme.presets.fullWidth,
    }
  },
  preview: {
    aspectRatio,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    zIndex: 1,
  },
  changeButton: {
    ...theme.presets.absolute,
    top: 8,
    right: 8,
    zIndex: 2,

    [theme.media.down('small')]: {
      top: 'auto',
      right: 'auto',
      left: theme.spacing.value(2),
      bottom: theme.spacing.value(4),
    }
  }
}), true)